import React, { useState, useEffect, useRef } from 'react';
import { 
  ShoppingBag, ChevronLeft, CreditCard, Coins, Banknote, 
  CheckCircle, ArrowRight, Loader2, Zap, Store, Sparkles, Flame, X, Download, Mail
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCodeStyling from 'qr-code-styling';
import { db } from '../../services/firebase'; 
import { doc, getDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth'; 

export const HypeCommerce = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const { user, profile } = useAuth(); 

  const [brand, setBrand] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const userCredits = profile?.cred || 0; 

  const [cart, setCart] = useState<{item: any, qty: number, size?: string}[]>([]);
  const [selectedSizes, setSelectedSizes] = useState<Record<string, string>>({});
  const [view, setView] = useState<'shop' | 'checkout' | 'success'>('shop');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'credits' | 'cash' | null>(null);
  
  // 🟢 NEW: State for the receipt contact (defaults to user profile if logged in)
  const [contactInfo, setContactInfo] = useState(profile?.email || profile?.phone || '');
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [ticketData, setTicketData] = useState<any>(null);
  const qrRef = useRef<HTMLDivElement>(null);

  // ============================================================================
  // FETCH REAL DATA
  // ============================================================================
  useEffect(() => {
    const fetchPopUpData = async () => {
      if (!brandId) return;
      setIsLoading(true);

      try {
        let brandData: any = null;
        const brandSnap = await getDoc(doc(db, 'users', brandId));
        
        if (brandSnap.exists()) {
          brandData = { id: brandSnap.id, ...brandSnap.data() };
        } else {
          const altBrandSnap = await getDoc(doc(db, 'brands', brandId));
          if (altBrandSnap.exists()) brandData = { id: altBrandSnap.id, ...altBrandSnap.data() };
        }

        if (brandData) setBrand(brandData);

        const productsRef = collection(db, 'products');
        const q = query(productsRef, where('businessId', '==', brandId));
        const prodSnap = await getDocs(q);
        
        const activeProducts = prodSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).filter((p: any) => p.status !== 'archived'); 

        setProducts(activeProducts);

      } catch (error) {
        console.error("Error fetching Pop-Up data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPopUpData();
  }, [brandId]);

  const cartTotal = cart.reduce((total, cartItem) => {
    const price = cartItem.item.salePrice > 0 ? cartItem.item.salePrice : (cartItem.item.price || cartItem.item.regularPrice || 0);
    return total + (price * cartItem.qty);
  }, 0);

  const handleSizeSelect = (itemId: string, size: string) => {
    setSelectedSizes(prev => ({ ...prev, [itemId]: size }));
  };

  const handleAddToCart = (item: any) => {
    const sizeRequired = item.sizes && item.sizes.length > 0;
    const chosenSize = selectedSizes[item.id];

    if (sizeRequired && !chosenSize) {
      alert("Select your size before copping!");
      return;
    }

    setCart(prev => {
      const existing = prev.find(p => p.item.id === item.id && p.size === chosenSize);
      if (existing) {
        return prev.map(p => (p.item.id === item.id && p.size === chosenSize) ? { ...p, qty: p.qty + 1 } : p);
      }
      return [...prev, { item, qty: 1, size: chosenSize }];
    });
  };

  const handleRemove = (itemId: string, size?: string) => {
    setCart(prev => prev.filter(p => !(p.item.id === itemId && p.size === size)));
  };

  // ============================================================================
  // PROCESS REAL ORDER
  // ============================================================================
  const handleConfirmOrder = async () => {
    if (!paymentMethod) return;
    
    // 🟢 REQUIRE CONTACT INFO IF NOT CASH
    if (paymentMethod !== 'cash' && !contactInfo.trim()) {
        alert("Please enter an email or phone number for your receipt.");
        return;
    }

    if (paymentMethod === 'credits' && userCredits < cartTotal) {
        alert("Insufficient Scoop Credits for this transaction.");
        return;
    }

    setIsProcessing(true);

    try {
      const orderNumber = `HYPE-${Math.floor(Math.random() * 900000) + 100000}`;
      
      // 🟢 SAVING THE TRANSACTION (No shipping address needed)
      await addDoc(collection(db, 'orders'), {
          orderNumber,
          businessId: brandId,
          businessName: brand?.businessName || brand?.displayName || 'Brand',
          userId: user?.uid || 'guest',
          userName: profile?.displayName || 'Guest User',
          receiptContact: contactInfo, // Saves the email/phone for the receipt
          type: 'pop_up_event',
          status: paymentMethod === 'cash' ? 'pending_payment' : 'paid_awaiting_pickup',
          paymentMethod: paymentMethod,
          items: cart,
          total: cartTotal,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
      });

      setTicketData({
        orderNumber,
        timestamp: new Date(),
        method: paymentMethod,
        items: cart,
        total: cartTotal,
        qrData: `hype_verify_${orderNumber}_${brand?.id}` 
      });
      
      setView('success');
    } catch (error) {
        console.error("Failed to process Pop-Up order:", error);
        alert("Something went wrong. Please try again.");
    } finally {
        setIsProcessing(false);
    }
  };

  // 🟢 NATIVE DOWNLOAD / PRINT FUNCTION
  const handleDownloadTicket = () => {
      window.print();
  };

  useEffect(() => {
    if (view === 'success' && ticketData && qrRef.current) {
      const qrCode = new QRCodeStyling({
        width: 180,
        height: 180,
        data: ticketData.qrData,
        dotsOptions: { type: "rounded", color: "#ffffff" },
        cornersSquareOptions: { type: "extra-rounded", color: "#ffffff" },
        backgroundOptions: { color: "transparent" },
      });
      qrRef.current.innerHTML = '';
      qrCode.append(qrRef.current);
    }
  }, [view, ticketData]);

  // ============================================================================
  // RENDER STATES
  // ============================================================================
  if (isLoading) {
      return (
          <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
              <Loader2 size={32} className="animate-spin mb-4" />
              <p className="font-black italic uppercase tracking-widest text-xs">Loading Hype Commerce...</p>
          </div>
      );
  }

  if (!brand) {
      return (
          <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white p-6 text-center">
              <Store size={48} className="mb-4 text-zinc-600" />
              <h1 className="text-2xl font-black uppercase italic tracking-tighter mb-2">Pop-Up Not Found</h1>
              <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest mb-8">This brand event might have ended.</p>
              <button onClick={() => navigate('/')} className="px-6 py-3 bg-white text-black font-black uppercase tracking-widest text-xs rounded-full">Return Home</button>
          </div>
      );
  }

  // ============================================================================
  // VIEW: SUCCESS / DIGITAL TICKET
  // ============================================================================
  if (view === 'success' && ticketData) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center p-6 animate-in fade-in duration-500 overflow-y-auto">
        <style>
            {`
              @media print {
                  body * { visibility: hidden; }
                  #digital-ticket, #digital-ticket * { visibility: visible; }
                  #digital-ticket { position: absolute; left: 0; top: 0; width: 100%; border: none; box-shadow: none; }
                  .no-print { display: none !important; }
              }
            `}
        </style>
        
        {/* 🟢 THE DIGITAL TICKET (Targeted for printing/saving) */}
        <div id="digital-ticket" className="w-full max-w-sm bg-zinc-950 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl relative mt-4">
          <div className="p-5 text-center border-b border-zinc-900">
            <div className="w-12 h-12 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-3 border border-green-500/50">
              <CheckCircle size={24} />
            </div>
            <h2 className="text-xl font-black text-white uppercase tracking-tighter italic">Bag Secured</h2>
            <p className="text-zinc-500 text-[9px] font-mono uppercase tracking-widest mt-1">Show this to the vendor</p>
          </div>

          <div className="p-6 flex flex-col items-center justify-center bg-zinc-900/50 relative">
            <div className="bg-black p-3 rounded-2xl border border-zinc-800 mb-4">
              <div ref={qrRef} />
            </div>
            <h1 className="text-3xl font-black text-white tracking-widest">{ticketData.orderNumber}</h1>
            <p className="text-zinc-500 text-[10px] uppercase font-bold mt-2">Brand: {brand.businessName || brand.name}</p>
          </div>

          <div className="p-5 bg-black border-t border-zinc-900 flex justify-between items-center">
            <div>
              <p className="text-zinc-500 text-[8px] uppercase tracking-widest font-black mb-1">Total Paid</p>
              <p className="text-white font-black text-base">R {ticketData.total}</p>
            </div>
            <div className="px-3 py-1.5 bg-zinc-900 rounded border border-zinc-800 text-[9px] font-bold text-green-400 uppercase tracking-widest flex items-center gap-1">
              <Zap size={10} /> {ticketData.method === 'cash' ? 'Pay at counter' : 'Paid & Ready'}
            </div>
          </div>
        </div>

        {/* 🟢 ACTION BUTTONS (Hidden during print) */}
        <div className="w-full max-w-sm mt-8 space-y-3 no-print animate-in slide-in-from-bottom-8 delay-300">
          <button onClick={handleDownloadTicket} className="w-full py-4 bg-zinc-900 text-white border border-zinc-800 font-black uppercase tracking-widest text-[10px] rounded-xl hover:bg-zinc-800 transition-colors flex items-center justify-center gap-2">
            <Download size={16} /> Save / Screenshot Ticket
          </button>
          
          <button onClick={() => navigate('/')} className="w-full py-5 bg-white text-black font-black uppercase tracking-[0.2em] text-xs rounded-xl hover:bg-zinc-200 transition-colors active:scale-95 flex items-center justify-center gap-2">
            Enter ScoopHub <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // VIEW: SHOP & SECURE BAG
  // ============================================================================
  return (
    <div className="w-full min-h-screen bg-[#050505] flex flex-col max-w-lg mx-auto border-x border-zinc-900 relative">
      {/* Header */}
      <div className="p-5 border-b border-zinc-900 bg-black/80 backdrop-blur-md sticky top-0 z-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {view === 'checkout' && <button onClick={() => setView('shop')} className="p-2 -ml-2 bg-zinc-900 rounded-full text-white"><ChevronLeft size={16} /></button>}
          <div className="w-10 h-10 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden">
            <img src={brand.photoURL || brand.logo || brand.avatar || 'https://placehold.co/150x150/111/fff?text=BRAND'} className="w-full h-full object-cover grayscale" alt="Logo" />
          </div>
          <div>
            <h1 className="text-white font-black text-sm uppercase tracking-widest truncate max-w-[150px]">{brand.businessName || brand.displayName || brand.name}</h1>
            <p className="text-zinc-500 text-[9px] font-mono flex items-center gap-1 uppercase"><Flame size={10} className="text-orange-500"/> Hype Commerce • {brand.location || 'Local Pop-Up'}</p>
          </div>
        </div>
        {view === 'shop' && (
          <button onClick={() => setView('checkout')} className="relative p-2 text-white">
            <ShoppingBag size={20} />
            {cart.length > 0 && <span className="absolute top-0 right-0 w-4 h-4 bg-white text-black text-[9px] font-black flex items-center justify-center rounded-full">{cart.reduce((total, item) => total + item.qty, 0)}</span>}
          </button>
        )}
      </div>

      {/* Shop View */}
      {view === 'shop' && (
        <div className="flex-1 p-4 grid grid-cols-2 gap-4 pb-32 animate-in fade-in">
          {products.length === 0 ? (
              <div className="col-span-2 text-center py-20 text-zinc-600">
                  <p className="font-mono text-xs uppercase tracking-widest">No products loaded for this event.</p>
              </div>
          ) : products.map(item => {
            const inCart = cart.filter(c => c.item.id === item.id).reduce((sum, c) => sum + c.qty, 0);
            const chosenSize = selectedSizes[item.id];
            const displayImage = item.image || (item.images && item.images[0]) || 'https://placehold.co/600x800/222/555?text=ITEM';
            const displayPrice = item.salePrice > 0 ? item.salePrice : (item.price || item.regularPrice || 0);

            return (
              <div key={item.id} className="bg-zinc-950 border border-zinc-900 rounded-xl overflow-hidden flex flex-col group relative">
                {(item.stock === 0 || item.quantity === 0) && (
                    <div className="absolute inset-0 bg-black/80 z-10 flex items-center justify-center backdrop-blur-sm">
                        <span className="text-white font-black uppercase tracking-widest border border-white px-3 py-1 -rotate-12">Sold Out</span>
                    </div>
                )}

                <div className="aspect-[4/5] bg-zinc-900 relative overflow-hidden">
                  <img src={displayImage} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                  {inCart > 0 && <div className="absolute top-2 right-2 bg-white text-black px-2 py-1 rounded-full text-[10px] font-black shadow-lg z-20">{inCart} STASHED</div>}
                </div>
                
                <div className="p-3 flex-1 flex flex-col justify-between">
                  <div className="mb-3">
                    <h3 className="text-white font-black text-[10px] uppercase truncate">{item.name || item.title}</h3>
                    <p className="text-zinc-400 font-mono text-[10px] mt-1">R {displayPrice}</p>
                  </div>
                  
                  {item.sizes && item.sizes.length > 0 && (
                    <div className="flex gap-1.5 mb-3 flex-wrap">
                      {item.sizes.map((size: string) => (
                        <button key={size} onClick={() => handleSizeSelect(item.id, size)} className={`flex-1 py-1 text-[9px] font-black border rounded transition-colors ${chosenSize === size ? 'bg-white text-black border-white' : 'bg-black text-zinc-500 border-zinc-800 hover:border-zinc-500'}`}>{size}</button>
                      ))}
                    </div>
                  )}
                  
                  <button 
                    disabled={item.stock === 0 || item.quantity === 0}
                    onClick={() => handleAddToCart(item)} 
                    className="w-full py-2.5 bg-zinc-900 text-white border border-zinc-800 text-[9px] font-black uppercase tracking-widest rounded-lg hover:bg-white hover:text-black transition-colors active:scale-95 disabled:opacity-50"
                  >
                    Cop This
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Checkout View */}
      {view === 'checkout' && (
        <div className="flex-1 p-5 animate-in slide-in-from-right pb-32">
          <h2 className="text-xl font-black text-white uppercase italic tracking-tighter mb-4">Secure The Bag</h2>
          
          <div className="bg-zinc-950 border border-zinc-900 rounded-2xl p-4 mb-6">
            <h3 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-4 border-b border-zinc-800 pb-2">Your Stash</h3>
            {cart.length === 0 ? <p className="text-zinc-600 text-xs font-mono uppercase text-center py-4">Stash is empty</p> : (
              <div className="space-y-4">
                {cart.map((c, idx) => {
                  const displayImage = c.item.image || (c.item.images && c.item.images[0]);
                  const displayPrice = c.item.salePrice > 0 ? c.item.salePrice : (c.item.price || c.item.regularPrice);
                  
                  return (
                  <div key={`${c.item.id}-${c.size}-${idx}`} className="flex gap-4 items-center">
                    <img src={displayImage} className="w-16 h-16 rounded-lg object-cover grayscale" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-xs uppercase truncate">{c.item.name || c.item.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {c.size && <span className="bg-zinc-800 text-white text-[9px] px-1.5 py-0.5 rounded font-bold uppercase">Size {c.size}</span>}
                        <span className="text-zinc-500 text-[10px] font-mono">QTY: {c.qty}</span>
                      </div>
                      <p className="text-white font-black text-sm mt-1">R {displayPrice * c.qty}</p>
                    </div>
                    <button onClick={() => handleRemove(c.item.id, c.size)} className="text-zinc-600 hover:text-red-500 p-2"><X size={16}/></button>
                  </div>
                )})}
              </div>
            )}
            {cart.length > 0 && <div className="mt-5 pt-4 border-t border-zinc-800 flex justify-between items-center"><span className="text-zinc-400 text-[10px] font-black uppercase tracking-widest">Total</span><span className="text-white font-black text-xl">R {cartTotal}</span></div>}
          </div>

          <div className="space-y-6">
            {/* 🟢 RECEIPT CONTACT INPUT */}
            <div>
                <h3 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3">Digital Receipt</h3>
                <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input 
                        type="text" 
                        placeholder="Email or Phone Number"
                        value={contactInfo}
                        onChange={(e) => setContactInfo(e.target.value)}
                        className="w-full bg-black border border-zinc-800 rounded-xl py-4 pl-10 pr-4 text-white text-sm outline-none focus:border-white transition-colors"
                    />
                </div>
            </div>

            {/* 🟢 DYNAMIC PAYMENT METHODS */}
            <div>
                <h3 className="text-[10px] font-black text-zinc-500 uppercase tracking-widest mb-3">Select Payment</h3>
                <div className="space-y-3">
                    <button onClick={() => setPaymentMethod('credits')} className={`w-full p-4 flex items-center justify-between rounded-xl border transition-all ${paymentMethod === 'credits' ? 'bg-zinc-900 border-white text-white' : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
                        <div className="flex items-center gap-3"><Coins size={18} className={paymentMethod === 'credits' ? 'text-yellow-500' : ''}/><span className="font-black text-xs uppercase tracking-widest">Scoop Credits</span></div>
                        <div className="text-right"><span className="block text-[10px] font-mono">Bal: R{userCredits}</span></div>
                    </button>
                    
                    <button onClick={() => setPaymentMethod('card')} className={`w-full p-4 flex items-center justify-between rounded-xl border transition-all ${paymentMethod === 'card' ? 'bg-zinc-900 border-white text-white' : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
                        <div className="flex items-center gap-3"><CreditCard size={18} className={paymentMethod === 'card' ? 'text-blue-500' : ''}/><span className="font-black text-xs uppercase tracking-widest">Card / Apple Pay</span></div>
                        <div className="text-[9px] bg-zinc-800 px-2 py-1 rounded">{brand?.paygateName || 'Yoco'}</div>
                    </button>
                    
                    <button onClick={() => setPaymentMethod('cash')} className={`w-full p-4 flex items-center justify-between rounded-xl border transition-all ${paymentMethod === 'cash' ? 'bg-zinc-900 border-white text-white' : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600'}`}>
                        <div className="flex items-center gap-3"><Banknote size={18} className={paymentMethod === 'cash' ? 'text-green-500' : ''}/><span className="font-black text-xs uppercase tracking-widest">Cash at Counter</span></div>
                        <span className="text-[10px] font-mono italic">Pay at pickup</span>
                    </button>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Bars */}
      {view === 'shop' && cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black via-black to-transparent z-40 max-w-lg mx-auto animate-in slide-in-from-bottom-10">
          <button onClick={() => setView('checkout')} className="w-full py-4 bg-white text-black rounded-xl font-black uppercase tracking-[0.2em] text-xs shadow-[0_0_30px_rgba(255,255,255,0.15)] flex items-center justify-between px-6 hover:bg-zinc-200 transition-colors">
            <span>Secure Bag</span><span className="flex items-center gap-2">R {cartTotal} <ArrowRight size={14} /></span>
          </button>
        </div>
      )}

      {view === 'checkout' && cart.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-black border-t border-zinc-900 z-40 max-w-lg mx-auto">
          <button disabled={!paymentMethod || isProcessing} onClick={handleConfirmOrder} className={`w-full py-4 rounded-xl font-black uppercase tracking-[0.2em] text-xs transition-all flex items-center justify-center gap-2 ${paymentMethod ? 'bg-white text-black shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:bg-zinc-200 active:scale-95' : 'bg-zinc-900 text-zinc-600 cursor-not-allowed'}`}>
            {isProcessing ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            {isProcessing ? 'Processing...' : 'Pay & Get Ticket'}
          </button>
        </div>
      )}
    </div>
  );
};